import compact from "lodash/compact";
import { useTranslation } from "react-i18next";

import {
  Button,
  ButtonAnchor,
  ButtonLink,
} from "@/components/Interface/Button/Button";
import { Modal } from "@/components/Interface/Modal/Modal";
import { ButtonGroupFlexBox } from "@/components/Layout/ButtonGroupFlexBox/ButtonGroupFlexBox";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { SimCustomerInfoAddESIMNotAllowedReasonItem } from "@/services/model";
import { AddDeviceBlockedReason } from "@/utils/customerUtils";
import { TranslationKey } from "@/utils/translationKey";

type Props = {
  reasons: AddDeviceBlockedReason[];
  onClose: () => void;
};

const reasonKey: Partial<Record<AddDeviceBlockedReason, TranslationKey>> = {
  [SimCustomerInfoAddESIMNotAllowedReasonItem.TooManySIMs]:
    "portal:add-device.simSelection.reasons.TooManySIMs",
  [SimCustomerInfoAddESIMNotAllowedReasonItem.IDCheckOpen]:
    "portal:add-device.simSelection.reasons.IDCheckOpen",
  [SimCustomerInfoAddESIMNotAllowedReasonItem.DunningLevel3]:
    "portal:common.openBillReason",
  [SimCustomerInfoAddESIMNotAllowedReasonItem.DunningProcessOpen]:
    "Onboarding.sections.sim-selection.dunningProcessOpenText",
};

export const AddDeviceBlockedModal = ({ reasons, onClose }: Props) => {
  const { t } = useTranslation();

  const hasTooManySims = reasons.includes(
    SimCustomerInfoAddESIMNotAllowedReasonItem.TooManySIMs,
  );
  const hasIdCheckOpen = reasons.includes(
    SimCustomerInfoAddESIMNotAllowedReasonItem.IDCheckOpen,
  );
  const hasDunning =
    reasons.includes(
      SimCustomerInfoAddESIMNotAllowedReasonItem.DunningLevel3,
    ) ||
    reasons.includes(
      SimCustomerInfoAddESIMNotAllowedReasonItem.DunningProcessOpen,
    );

  const translatedReasons = compact<string>(
    reasons.map((reason) =>
      reasonKey[reason] ? t(reasonKey[reason]) : undefined,
    ),
  );

  const contextualActions = (
    <>
      {hasTooManySims && (
        <ButtonAnchor
          className="tablet:w-fit"
          fullWidth
          href={t("Onboarding.sections.sim-selection.upgradeSimLimitLink")}
          inverted
          variant="secondary"
        >
          {t("Onboarding.sections.sim-selection.upgradeSimLimit")}
        </ButtonAnchor>
      )}
      {hasDunning && (
        <ButtonLink
          className="tablet:w-fit"
          fullWidth
          variant="secondary"
          inverted
          to={portalRoutes.documents.bills}
          replace
        >
          {t("portal:add-device.blockedModal.viewBills")}
        </ButtonLink>
      )}
      {hasIdCheckOpen && (
        <ButtonLink
          className="tablet:w-fit"
          fullWidth
          variant="secondary"
          inverted
          to={portalRoutes.cockpit.idCheck}
        >
          {t("portal:cockpit.id-banner.open.button")}
        </ButtonLink>
      )}
    </>
  );

  const hasContextualActions = hasTooManySims || hasDunning || hasIdCheckOpen;

  const closeButton = (
    <Button variant="primary" inverted onClick={onClose}>
      {t("Common.label.gotIt")}
    </Button>
  );

  return (
    <Modal
      title={t("portal:common.tooltip.actionNotPossible.title")}
      onClose={onClose}
      hasScroll={false}
      variant="dark"
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <p className="text-body-base text-foreground-primary-inverse">
            {t("portal:common.tooltip.actionNotPossible.text", {
              count: translatedReasons.length,
            })}
          </p>
          {translatedReasons.length > 0 && (
            <ul className="list-outside list-disc space-y-2 pl-4 text-body-base text-foreground-primary-inverse">
              {translatedReasons.map((reason) => (
                <li key={reason}>{reason}</li>
              ))}
            </ul>
          )}
        </div>

        <ButtonGroupFlexBox
          className="!mt-0"
          rightAligned
          forceButtonWidth
          primaryButton={closeButton}
          secondaryButton={
            hasContextualActions ? (
              <div className="flex flex-col gap-3 tablet:flex-row tablet:flex-wrap tablet:gap-4 tablet:[&>div]:w-auto">
                {contextualActions}
              </div>
            ) : undefined
          }
        />
      </div>
    </Modal>
  );
};
